import { useTheme } from '@moment/design-system-ui/theme'
import { cva } from 'class-variance-authority'

import { ButtonVariant } from '@/components/atomic/button/variants'
import { Mode } from '@/types/enums/mode'
import { cn } from '@/utils/shadcn/utils'

export enum SpinnerSize {
  Small = 'small',
  Large = 'large',
}

// Spinner variants using cva
const spinnerVariants = cva(
  // Base styles that apply to all variants
  'flex justify-center items-center',
  {
    variants: {
      size: {
        [SpinnerSize.Small]: 'h-4 w-4',
        [SpinnerSize.Large]: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: SpinnerSize.Small,
    },
  },
)

// SVG size variants
const svgSizeVariants = cva('animate-spin', {
  variants: {
    size: {
      [SpinnerSize.Small]: 'w-8 h-8',
      [SpinnerSize.Large]: 'w-10 h-10',
    },
  },
  defaultVariants: {
    size: SpinnerSize.Small,
  },
})

// Track color variants
const trackColorVariants = cva('', {
  variants: {
    variant: {
      [ButtonVariant.Primary]: 'text-moment-white-100/30',
      [ButtonVariant.Secondary]:
        'text-moment-grey-200 dark:text-moment-white-100/30',
      [ButtonVariant.Danger]:
        'text-moment-red-500/40 dark:text-moment-red-400/40',
      [ButtonVariant.Ghost]: 'text-moment-blue-grey-500/30',
      [ButtonVariant.Badge]: 'text-moment-blue-grey-500/30', // Added Badge variant
    },
    disabled: {
      true: 'text-moment-grey-600/20 dark:text-moment-grey-400/20',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: ButtonVariant.Primary,
      disabled: true,
      className: 'text-moment-grey-600/30',
    },
  ],
  defaultVariants: {
    variant: ButtonVariant.Secondary,
    disabled: false,
  },
})

// Fill color mapping for different variants
const fillColorMap = {
  [ButtonVariant.Primary]: {
    default: 'text-moment-white-100 dark:text-moment-teal-300',
    disabled: 'text-moment-blue-grey-600 dark:text-moment-grey-400',
  },
  [ButtonVariant.Secondary]:
    'text-moment-blue-grey-600 dark:text-moment-teal-300',
  [ButtonVariant.Danger]: 'text-moment-red-400',
  [ButtonVariant.Ghost]: 'text-moment-blue-grey-500',
  [ButtonVariant.Badge]: 'text-moment-blue-grey-500', // Added Badge variant
}

interface LoadingSpinnerProps {
  variant?: ButtonVariant
  size?: SpinnerSize | string | null // Accept string for compatibility with Button
  disabled?: boolean
  className?: string
  mode?: Mode // for Storybook to control dark/light mode for testing
}

/**
 * @deprecated Use `Spinner` from `@moment/design-system-ui/spinner` instead.
 */
const LoadingSpinner = ({
  variant = ButtonVariant.Secondary,
  size = SpinnerSize.Small,
  disabled = false,
  className,
  mode,
}: LoadingSpinnerProps) => {
  // Fall back to specified mode if not provided by hook
  const { theme: resolvedTheme = mode } = useTheme()
  const isDark = resolvedTheme === Mode.Dark

  // Determine fill color class based on variant
  let fillColorClass: string

  if (variant === ButtonVariant.Primary && disabled) {
    fillColorClass = fillColorMap[ButtonVariant.Primary].disabled
  } else if (variant === ButtonVariant.Primary) {
    fillColorClass = fillColorMap[ButtonVariant.Primary].default
  } else {
    fillColorClass = fillColorMap[
      variant as keyof typeof fillColorMap
    ] as string
  }

  // Extract light and dark mode classes for the fill style
  const parts = fillColorClass.split(' ')
  const lightModeClass = parts[0]
  const darkModeClass =
    parts.find((p) => p.startsWith('dark:'))?.replace('dark:', '') ||
    lightModeClass

  // Use the appropriate color class based on current theme
  const activeColorClass = isDark ? darkModeClass : lightModeClass

  // Create the fill style using the active color class
  const fillStyle = {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    fill: `var(${activeColorClass!.replace('text-moment-', '--color-moment-')})`,
  }

  // Handle null values for size
  const sizeValue = size === null ? SpinnerSize.Small : size

  return (
    <div
      className={cn(
        spinnerVariants({ size: sizeValue as SpinnerSize }),
        className,
      )}
    >
      <svg
        aria-hidden="true"
        className={cn(
          svgSizeVariants({ size: sizeValue as SpinnerSize }),
          trackColorVariants({ variant, disabled }),
        )}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          style={fillStyle}
        />
      </svg>
    </div>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default LoadingSpinner
